import React, { useState } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItemButton,
  ListItemText, Box, CssBaseline, Divider, Menu, MenuItem, Avatar,
  Collapse, useMediaQuery
} from '@mui/material';
import { Menu as MenuIcon, AccountCircle, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import Dashboard from './Dashboard';
import ItemLocation from './ItemLocation';
import ProductSetup from './ProductSetup';
import Transaction from './Transaction';
import MachineCustomer from './MachineCustomer';
import MachineConfig from './MachineConfig';
import MachineLocation from './MachineLocation';
import ProductPurchase from './ProductPurchase';
import ProductSupplier from './ProductSupplier';
import ItemInventory from './ItemInventory';
import ResetLocker from './ResetLocker';

const drawerWidth = 240;

const StaffLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [machineOpen, setMachineOpen] = useState(true);
  const [productOpen, setProductOpen] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/staff-login');
  };

  let user = {};
  try {
    const rawUser = localStorage.getItem('user');
    user = rawUser && rawUser !== 'undefined' ? JSON.parse(rawUser) : {};
  } catch (err) {
    user = {};
  }
  
  // ✅ Define this after parsing user
  const userName = user.name || user.email || 'Admin';

  const drawerContent = (
    <Box>
      <Toolbar />
      <Divider />
      <List>
        <ListItemButton component={NavLink} to="/staff" sx={{ '&.active': { backgroundColor: '#ddd' } }}>
          <ListItemText primary="Dashboard" />
        </ListItemButton>

        {/* MACHINE Section */}
        <ListItemButton onClick={() => setMachineOpen(!machineOpen)}>
          <ListItemText primary="MACHINE" />
          {machineOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={machineOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton component={NavLink} to="/staff/machine-customer" sx={{ pl: 4, '&.active': { backgroundColor: '#eee' } }}>
              <ListItemText primary="Machine Customer" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/staff/machine-location" sx={{ pl: 4, '&.active': { backgroundColor: '#eee' } }}>
              <ListItemText primary="Machine Location" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/staff/machine-config" sx={{ pl: 4, '&.active': { backgroundColor: '#eee' } }}>
              <ListItemText primary="Machine Config" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/staff/item-location" sx={{ pl: 4, '&.active': { backgroundColor: '#eee' } }}>
              <ListItemText primary="Item Location" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* PRODUCT Section */}
        <ListItemButton onClick={() => setProductOpen(!productOpen)}>
          <ListItemText primary="PRODUCT" />
          {productOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={productOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton component={NavLink} to="/staff/product-setup" sx={{ pl: 4, '&.active': { backgroundColor: '#eee' } }}>
              <ListItemText primary="Product Setup" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/staff/product-supplier" sx={{ pl: 4, '&.active': { backgroundColor: '#eee' } }}>
              <ListItemText primary="Product Supplier" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/staff/product-purchase" sx={{ pl: 4, '&.active': { backgroundColor: '#eee' } }}>
              <ListItemText primary="Product Purchase" />
            </ListItemButton>
          </List>
        </Collapse>

        <ListItemButton component={NavLink} to="/staff/transaction" sx={{ '&.active': { backgroundColor: '#ddd' } }}>
          <ListItemText primary="Transaction" />
        </ListItemButton>

        <ListItemButton component={NavLink} to="/staff/item-inventory" sx={{ '&.active': { backgroundColor: '#ddd' } }}>
          <ListItemText primary="Machine Inventory" />
        </ListItemButton>

        <ListItemButton component={NavLink} to="/staff/reset-locker" sx={{ '&.active': { backgroundColor: '#ddd' } }}>
          <ListItemText primary="Reset Locker" />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {isMobile && (
            <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Staff Portal</Typography>
          <IconButton color="inherit" onClick={handleMenuOpen}><AccountCircle /></IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem disabled>Logged in as: {userName}</MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' } }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { md: `calc(100% - ${drawerWidth}px)` } }}>
        <Toolbar />
        <Routes>
          <Route path="" element={<Dashboard />} />
          <Route path="machine-customer" element={<MachineCustomer />} />
          <Route path="machine-config" element={<MachineConfig />} />
          <Route path="machine-location" element={<MachineLocation />} />
          <Route path="item-location" element={<ItemLocation />} />
          <Route path="product-setup" element={<ProductSetup />} />
          <Route path="product-purchase" element={<ProductPurchase />} />
          <Route path="product-supplier" element={<ProductSupplier />} />
          <Route path="transaction" element={<Transaction />} />
          <Route path="item-inventory" element={<ItemInventory />} />
          <Route path="reset-locker" element={<ResetLocker />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default StaffLayout;



